import { registerAuthRuntime } from '@/api/runtime';
import { refreshSessionRequest } from '@/api/endpoints/auth';
import { useAuthStore } from './auth-store';

let inFlight: Promise<boolean> | null = null;

async function runRefresh(): Promise<boolean> {
  const { refreshToken } = useAuthStore.getState();
  if (!refreshToken) return false;
  try {
    const tokens = await refreshSessionRequest(refreshToken);
    useAuthStore.getState().setTokens(tokens);
    return true;
  } catch {
    return false;
  }
}

export function refreshSession(): Promise<boolean> {
  if (inFlight) return inFlight;
  const run = runRefresh();
  inFlight = run;
  // The latch is released from a callback on `run`, never from a `finally`
  // inside it. When the attempt finishes without ever yielding -- which is
  // exactly what happens when there is no refreshToken -- an inner `finally`
  // runs BEFORE the assignment above, so the assignment then overwrites the
  // null and leaves the latch closed over an already settled promise. From
  // that point on every caller gets that stale `false` back without a request
  // ever going out, and http.ts reads it as "the session is gone".
  const release = () => {
    if (inFlight === run) inFlight = null;
  };
  void run.then(release, release);
  return run;
}

export function installAuthRuntime(): void {
  registerAuthRuntime({
    getAccessToken: () => useAuthStore.getState().accessToken,
    refresh: refreshSession,
    onAuthFailure: () => useAuthStore.getState().clear(),
  });
}
